// src/components/payment/NonprofitDiscountCard.jsx
//
// Billing → Nonprofit discount. Owners and admins apply with the
// organization's legal name, EIN (or registration number outside the US),
// an optional TechSoup / Percent validation id and an optional proof
// document; then see the review status. Server: /api/nonprofit-discount
// (routes/nonprofitRoutes.js). Renders nothing for roles that can't use it
// (403) or before the migration is applied (409).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './NonprofitDiscountCard.css';

const API = '/api/nonprofit-discount';
const COUNTRIES = ['US', 'CA', 'GB', 'AU', 'NZ', 'IE'];
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp';

const EMPTY_FORM = {
  orgName: '', country: 'US', otherCountry: '', registrationNumber: '', useTaxProfileEin: true,
  validationProvider: '', validationId: '', website: '', applicantNote: '',
};

function formatDate(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export default function NonprofitDiscountCard() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(API);
      setState(data);
      setLoadError(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 404 || status === 409) setHidden(true);
      else setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;
  if (!state && !loadError) return null;

  const config = state?.config || {};
  const current = state?.current || null;
  if (state && !config.enabled && !current) return null;

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFieldErrors({});
    setFormError('');
    const country = form.country === 'other' ? form.otherCountry.trim().toUpperCase() : form.country;
    const fd = new FormData();
    fd.append('orgName', form.orgName);
    fd.append('country', country);
    const useEin = country === 'US' && state?.taxProfileEin && form.useTaxProfileEin;
    fd.append('useTaxProfileEin', useEin ? 'true' : 'false');
    if (!useEin) fd.append('registrationNumber', form.registrationNumber);
    if (form.validationId) {
      fd.append('validationProvider', form.validationProvider);
      fd.append('validationId', form.validationId);
    }
    if (form.website) fd.append('website', form.website);
    if (form.applicantNote) fd.append('applicantNote', form.applicantNote);
    if (file) fd.append('proof', file);
    try {
      await axios.post(API, fd);
      setShowForm(false);
      setForm(EMPTY_FORM);
      setFile(null);
      await load();
    } catch (err) {
      const body = err?.response?.data || {};
      setFieldErrors(body.fields || {});
      setFormError(body.error || t('billing.nonprofit.submitError', 'Could not submit the application. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!current) return;
    if (!window.confirm(t('billing.nonprofit.withdrawConfirm', 'Withdraw this application?'))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/${current.id}/withdraw`);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.error || t('billing.nonprofit.withdrawError', 'Could not withdraw the application.'));
    } finally {
      setBusy(false);
    }
  };

  const isUS = form.country === 'US';
  const err = (k) => fieldErrors[k] && <span className="npd-field-error" id={`npd-err-${k}`}>{fieldErrors[k]}</span>;
  const errProps = (k) => (fieldErrors[k] ? { 'aria-invalid': 'true', 'aria-describedby': `npd-err-${k}` } : {});

  return (
    <section className="billing-card npd-card" aria-labelledby="npd-title" data-testid="nonprofit-discount-card">
      <div className="card-header">
        <h2 id="npd-title">{t('billing.nonprofit.title', 'Nonprofit discount')}</h2>
        {current && (
          <span className={`npd-status npd-status--${current.status}`}>
            {t(`billing.nonprofit.status.${current.status}`, current.status)}
          </span>
        )}
      </div>
      <div className="card-body npd-body">
        {/* .surface: explicit light surface + theme defense (styles/surfaces.css),
            so text stays dark-on-light inside the dark-theme billing card. */}
        <div className="surface npd-surface">
        {loadError && (
          <p className="npd-text" role="alert">{t('billing.nonprofit.loadError', 'The nonprofit discount could not be loaded.')}</p>
        )}

        {state && (
          <>
            {current?.status === 'approved' && (
              <div className="npd-panel npd-panel--ok">
                <p className="npd-strong">
                  {t('billing.nonprofit.approvedText', { defaultValue: 'Approved — your organization gets {{percent}}% off.', percent: config.percentOff })}
                </p>
                {current.discountPending && !current.discountError && (
                  <p className="npd-text">{t('billing.nonprofit.pendingSubscription', 'The discount will be applied automatically when you choose a plan.')}</p>
                )}
                {current.discountError && (
                  <p className="npd-text">{t('billing.nonprofit.applyingText', 'We are finishing setting up the discount on your subscription. No action is needed from you.')}</p>
                )}
                {current.discountAppliedOn && (
                  <p className="npd-text">{t('billing.nonprofit.appliedOn', { defaultValue: 'Applied on {{date}}.', date: formatDate(current.discountAppliedOn) })}</p>
                )}
              </div>
            )}

            {current?.status === 'pending' && (
              <div className="npd-panel">
                <p className="npd-strong">
                  {t('billing.nonprofit.pendingText', { defaultValue: 'Under review since {{date}}.', date: formatDate(current.createdOn) })}
                </p>
                <p className="npd-text">
                  {t('billing.nonprofit.pendingDetail', { defaultValue: '{{org}} · registration ending {{last4}}. We will email you when it is decided.', org: current.orgName, last4: current.registrationLast4 })}
                </p>
                <button type="button" className="npd-btn npd-btn--ghost" onClick={withdraw} disabled={busy}>
                  {t('billing.nonprofit.withdraw', 'Withdraw application')}
                </button>
              </div>
            )}

            {current && ['rejected', 'revoked'].includes(current.status) && current.reviewNote && (
              <div className="npd-panel npd-panel--warn">
                <p className="npd-strong">{t(`billing.nonprofit.${current.status}Text`, current.status === 'rejected' ? 'Your last application was not approved.' : 'Your nonprofit discount has ended.')}</p>
                <p className="npd-text">{t('billing.nonprofit.reviewerNote', { defaultValue: 'Reviewer note: {{note}}', note: current.reviewNote })}</p>
              </div>
            )}

            {state.canApply && !showForm && (
              <div className="npd-intro">
                <p className="npd-text">
                  {t('billing.nonprofit.intro', { defaultValue: 'Eligible organizations get {{percent}}% off any plan.', percent: config.percentOff })}{' '}
                  {config.eligibility}
                </p>
                <button type="button" className="npd-btn" onClick={() => { setShowForm(true); setFormError(''); }}>
                  {t('billing.nonprofit.apply', 'Apply for the nonprofit discount')}
                </button>
              </div>
            )}

            {formError && !showForm && <p className="npd-form-error" role="alert">{formError}</p>}

            {showForm && (
              <form className="npd-form" onSubmit={submit} noValidate>
                <div className="npd-field">
                  <label htmlFor="npd-orgName">{t('billing.nonprofit.orgName', 'Organization legal name')}</label>
                  <input id="npd-orgName" type="text" value={form.orgName} onChange={set('orgName')} maxLength={200} required {...errProps('orgName')} />
                  {err('orgName')}
                </div>

                <div className="npd-field">
                  <label htmlFor="npd-country">{t('billing.nonprofit.country', 'Country')}</label>
                  <select id="npd-country" value={form.country} onChange={set('country')} {...errProps('country')}>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{t(`billing.nonprofit.countryName.${c}`, c)}</option>)}
                    <option value="other">{t('billing.nonprofit.countryOther', 'Another country')}</option>
                  </select>
                  {err('country')}
                </div>
                {form.country === 'other' && (
                  <div className="npd-field">
                    <label htmlFor="npd-countryCode">{t('billing.nonprofit.countryCode', 'Two-letter country code')}</label>
                    <input id="npd-countryCode" type="text" value={form.otherCountry} onChange={set('otherCountry')} maxLength={2} {...errProps('country')} />
                  </div>
                )}

                {isUS && state.taxProfileEin && (
                  <label className="npd-check">
                    <input type="checkbox" checked={form.useTaxProfileEin} onChange={set('useTaxProfileEin')} />
                    <span>{t('billing.nonprofit.useTaxEin', { defaultValue: 'Use the EIN in our Tax Center profile (ending {{last4}})', last4: state.taxProfileEin.last4 })}</span>
                  </label>
                )}
                {!(isUS && state.taxProfileEin && form.useTaxProfileEin) && (
                  <div className="npd-field">
                    <label htmlFor="npd-registrationNumber">{isUS ? t('billing.nonprofit.ein', 'EIN') : t('billing.nonprofit.registrationNumber', 'Charity or registration number')}</label>
                    <input
                      id="npd-registrationNumber"
                      type="text"
                      value={form.registrationNumber}
                      onChange={set('registrationNumber')}
                      placeholder={isUS ? '12-3456789' : ''}
                      autoComplete="off"
                      maxLength={40}
                      {...errProps('registrationNumber')}
                    />
                    {err('registrationNumber')}
                  </div>
                )}

                <div className="npd-row">
                  <div className="npd-field">
                    <label htmlFor="npd-validationProvider">{t('billing.nonprofit.validationProvider', 'Validation service (optional)')}</label>
                    <select id="npd-validationProvider" value={form.validationProvider} onChange={set('validationProvider')} {...errProps('validationProvider')}>
                      <option value="">{t('billing.nonprofit.validationNone', 'None')}</option>
                      <option value="techsoup">TechSoup</option>
                      <option value="percent">Percent</option>
                    </select>
                    {err('validationProvider')}
                  </div>
                  <div className="npd-field">
                    <label htmlFor="npd-validationId">{t('billing.nonprofit.validationId', 'Validation ID (optional)')}</label>
                    <input id="npd-validationId" type="text" value={form.validationId} onChange={set('validationId')} maxLength={100} />
                  </div>
                </div>

                <div className="npd-field">
                  <label htmlFor="npd-website">{t('billing.nonprofit.website', 'Website (optional)')}</label>
                  <input id="npd-website" type="url" value={form.website} onChange={set('website')} placeholder="https://" maxLength={255} {...errProps('website')} />
                  {err('website')}
                </div>

                <div className="npd-field">
                  <label htmlFor="npd-proof">{t('billing.nonprofit.proof', 'Proof of status (optional): determination letter or registration certificate')}</label>
                  <input id="npd-proof" type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} {...errProps('proof')} />
                  <small className="npd-hint">{t('billing.nonprofit.proofHint', 'PDF, PNG, JPG or WebP, up to 10 MB. Only On-Screen reviewers can see it.')}</small>
                  {err('proof')}
                </div>

                <div className="npd-field">
                  <label htmlFor="npd-note">{t('billing.nonprofit.note', 'Anything we should know? (optional)')}</label>
                  <textarea id="npd-note" value={form.applicantNote} onChange={set('applicantNote')} maxLength={1000} rows={3} />
                </div>

                {formError && <p className="npd-form-error" role="alert">{formError}</p>}

                <div className="npd-actions">
                  <button type="submit" className="npd-btn" disabled={busy}>
                    {busy ? t('billing.nonprofit.submitting', 'Submitting…') : t('billing.nonprofit.submit', 'Submit application')}
                  </button>
                  <button type="button" className="npd-btn npd-btn--ghost" onClick={() => { setShowForm(false); setFieldErrors({}); setFormError(''); }} disabled={busy}>
                    {t('billing.nonprofit.cancel', 'Cancel')}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
        </div>
      </div>
    </section>
  );
}
